import { getSession } from '@/lib/session';
import { capabilitiesForRole, normalizeRole } from '@/auth/permissions';
import './styles/base.css';
import './styles/neo.css';
import './styles/drawer.css';
import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { ToastProvider, useToast } from './components/ToastProvider';
import { setToast } from './lib/toast';
import { capture } from './lib/telemetry';
import { NeoBtnPrimary } from './ui/neo';
import { applyAuthFetch } from './lib/authFetch';
import { queryClient } from './lib/reactQueryClient';
import { refreshStoredSession } from './services/auth';
import { initializeTheme } from './lib/theme';

type WithChildren = React.PropsWithChildren<{}>;

const ToastConnector: React.FC<WithChildren> = ({ children }) => {
  const { enqueue } = useToast();
  React.useEffect(() => {
    setToast(enqueue);
  }, [enqueue]);
  return <>{children}</>;
};

const ErrorView: React.FC = () => (
  <div style={{ padding: 32, textAlign: 'center' }}>
    <h2>Ha ocurrido un error inesperado.</h2>
    <p style={{ margin: '16px 0', color: 'var(--muted)' }}>
      Intenta recargar la página o vuelve más tarde.
    </p>
    <NeoBtnPrimary type="button" onClick={() => window.location.reload()}>
      Recargar
    </NeoBtnPrimary>
  </div>
);

type ErrorBoundaryProps = React.PropsWithChildren<{ fallback: React.ReactNode }>;

class AppErrorBoundary extends React.Component<ErrorBoundaryProps, { hasError: boolean }> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo): void {
    capture(error, { componentStack: info.componentStack });
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

initializeTheme();

async function bootstrap() {
  applyAuthFetch();

  if (import.meta.env.DEV && typeof window !== 'undefined') {
    const { enableInboxMock } = await import('./mocks/inboxMock');
    enableInboxMock();
  }

  createRoot(document.getElementById('root')!).render(
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <ToastConnector>
          <AppErrorBoundary fallback={<ErrorView />}>
            <App />
          </AppErrorBoundary>
        </ToastConnector>
      </ToastProvider>
    </QueryClientProvider>,
  );

  if (typeof window !== 'undefined') {
    // Refresh in the background so a slow mobile network never leaves KORA blank.
    void refreshStoredSession();

    const startBackgroundServices = () => {
      if (!capabilitiesForRole(normalizeRole(getSession()?.user?.role)).canViewAdminReports) return;
      void import('./services/scheduler').then(({ initializeWeeklySummaryScheduler }) =>
        initializeWeeklySummaryScheduler(),
      );
    };
    const requestIdle = window.requestIdleCallback?.bind(window);
    if (typeof requestIdle === 'function') {
      requestIdle(startBackgroundServices, { timeout: 3_000 });
    } else {
      globalThis.setTimeout(startBackgroundServices, 1_500);
    }
  }

  if (import.meta.env.DEV && typeof window !== 'undefined') {
    const { enableOverflowDetector } = await import('./lib/overflowDetector');
    enableOverflowDetector();
  }
}

void bootstrap();
